'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X, CreditCard, Globe, Zap, ChevronDown, Check, Loader2, ExternalLink
} from 'lucide-react';
import { api } from '@/components/AuthProvider'; 
import { useScrollLock } from '@/app/hooks/useScrollLock';
import { useLanguage } from '@/components/LanguageProvider';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  targetAmount?: number;
  onSuccess?: () => void; 
}

const RATES: Record<string, number> = { RUB: 1, UAH: 0.42, USD: 0.011, EUR: 0.01 };
const SIGNS: Record<string, string> = { RUB: '₽', UAH: '₴', USD: '$', EUR: '€' };
const PACKS = [100, 300, 500, 1000];

const PAYMENT_METHODS = [
  { id: 'easydonate', name: 'EasyDonate', descKey: 'easydonateDesc' as const, icon: CreditCard, currencies: ['RUB'] },
  { id: 'tebex', name: 'Tebex', descKey: 'tebexDesc' as const, icon: Globe, currencies: ['USD', 'EUR', 'UAH'] },
  { id: 'paypal', name: 'PayPal', descKey: 'paypalDesc' as const, icon: Globe, currencies: ['USD', 'EUR', 'UAH'] },
  { id: 'lava', name: 'Lava.top', descKey: 'lavaDesc' as const, icon: Zap, currencies: ['RUB', 'USD', 'EUR', 'UAH'] }
];

const LAVA_IDS: Record<number, string> = {
  100: '329e95b1-347f-42d7-9c8f-ea9a93c69b3e',
  300: 'd6edb5d7-99b9-4da7-b89c-356d0747828b',
  500: '2ea1e75b-92bc-4a16-932d-0270ad7396db',
  1000: 'a90c6373-f708-4281-92ef-bb14d8aa629d'
};

const TEBEX_IDS: Record<number, string> = {
  100: '6412821',
  300: '6412826',
  500: '6412829',
  1000: '6412841'
};

const PaymentButton = React.memo(({
  method, amount, currency, description, onSelect
}: {
  method: typeof PAYMENT_METHODS[0], amount: number, currency: string, description: string, onSelect: (m: any) => void
}) => {
  let methodCurrency = currency;
  if (method.id === 'tebex' && currency !== 'EUR') methodCurrency = 'USD';
  
  const methodRate = RATES[methodCurrency] || RATES['USD'];
  const methodSign = SIGNS[methodCurrency] || '$';
  const displayPrice = (amount * methodRate).toFixed(2);

  return (
    <button
      onClick={() => onSelect(method)}
      className="w-full p-4 flex items-center justify-between text-left bg-[#121212] hover:bg-[#2a2a2a] border border-transparent rounded-xl transition-colors cursor-pointer outline-none focus:bg-[#2a2a2a]"
    >
      <div className="flex items-center gap-4">
        <method.icon size={22} className="text-zinc-400" />
        <div>
          <div className="text-white font-medium text-sm flex items-center gap-2">
            {method.name}
            {method.id === 'easydonate' && (
              <img 
                src="https://flagcdn.com/w40/ru.png" alt="RU" loading="lazy"
                className="w-3.5 h-2.5 object-cover rounded-sm opacity-80"
              />
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{description}</div>
        </div>
      </div>
      <div className="text-right">
        <span className="text-white font-medium">
          {displayPrice} {methodSign}
        </span>
      </div>
    </button>
  );
});
PaymentButton.displayName = 'PaymentButton';

export default function DepositModal({ isOpen, onClose, user, targetAmount = 0, onSuccess }: DepositModalProps) {
  const { t } = useLanguage();
  const [amount, setAmount] = useState<number>(100);
  const [currency, setCurrency] = useState<string>('RUB');
  const [country, setCountry] = useState<string>('RU');
  
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [waitingForPayment, setWaitingForPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  const [showAllMethods, setShowAllMethods] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');

  const initialCrystalsRef = useRef(0);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    
    initialCrystalsRef.current = user?.crystals || 0;

    const required = targetAmount > initialCrystalsRef.current ? targetAmount - initialCrystalsRef.current : 0;
    if (required > 0) {
        const closestPack = PACKS.find(p => p >= required) || PACKS[PACKS.length - 1];
        setAmount(closestPack);
    } else {
        setAmount(100);
    }

    setShowAllMethods(false);
    setIsLoadingCurrency(true);
    setIsProcessing(false);
    setWaitingForPayment(false);
    setPaymentSuccess(false);
    setPaymentUrl('');

    const controller = new AbortController();
    api.get('https://sg-public-api.royolands.com/rpweb/region', { signal: controller.signal })
      .then(res => {
        if (res.data.currency) setCurrency(res.data.currency);
        if (res.data.countryCode) setCountry(res.data.countryCode.toUpperCase());
      })
      .catch(() => setCurrency('EUR'))
      .finally(() => setIsLoadingCurrency(false));

    return () => controller.abort();
  }, [isOpen, targetAmount, user?.crystals]);

  useEffect(() => {
      let interval: NodeJS.Timeout;
      if (waitingForPayment && !paymentSuccess) {
          interval = setInterval(async () => {
              try {
                  const res = await api.get('https://sg-public-api.royolands.com/rpweb/user/balance');
                  const currentCrystals = res.data.crystals;
                  
                  if (currentCrystals >= initialCrystalsRef.current + amount) {
                      setPaymentSuccess(true);
                      setWaitingForPayment(false);
                      clearInterval(interval);
                      setTimeout(() => {
                          if (onSuccess) onSuccess();
                      }, 1500);
                  }
              } catch (e) {
                  // Игнорируем ошибки при поллинге
              }
          }, 2000);
      }
      return () => clearInterval(interval);
  }, [waitingForPayment, paymentSuccess, amount, onSuccess]);

  const price = useMemo(() => (amount * (RATES[currency] || RATES['USD'])).toFixed(2), [amount, currency]);
  const sign = SIGNS[currency] || '$';

  const { primaryMethods, secondaryMethods } = useMemo(() => {
    const filtered = PAYMENT_METHODS.filter(m => !(m.id === 'easydonate' && ['UA', 'LV'].includes(country)));
    const primary = filtered.filter(m => m.currencies.includes(currency));
    const secondary = filtered.filter(m => !m.currencies.includes(currency));
    return { primaryMethods: primary, secondaryMethods: secondary };
  }, [country, currency]);

  const proceedToPay = async (methodId: string, finalAmount: number) => {
    setIsProcessing(true);
    try {
      const currentRate = RATES[currency] || RATES['EUR'];
      const fiatPrice = Number((finalAmount * currentRate).toFixed(2));

      let payload: any = { 
          amount: finalAmount, 
          fiatAmount: fiatPrice, 
          currency: currency,
          targetNickname: user?.nickname
      };
      
      let endpoint = `https://sg-public-api.royolands.com/rpweb/payment/${methodId}/create`;

      if (methodId === 'tebex' && TEBEX_IDS[finalAmount]) {
          payload.productId = TEBEX_IDS[finalAmount];
      }

      if (methodId === 'lava' || methodId === 'paypal') {
        endpoint = 'https://sg-public-api.royolands.com/rpweb/payment/lava/create';
        
        if (LAVA_IDS[finalAmount]) {
            payload.productId = LAVA_IDS[finalAmount];
        }

        if (methodId === 'paypal') { 
            payload.currency = 'USD'; 
            payload.paymentProvider = 'PAYPAL'; 
        }
      }

      const res = await api.post(endpoint, payload);
      const data = res.data;
      
      if (data.paymentUrl) {
          setPaymentUrl(data.paymentUrl);
          window.open(data.paymentUrl, '_blank', 'noopener,noreferrer');
          setWaitingForPayment(true);
      } else {
          throw new Error(data.error || t.depositModal.errors.initError);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || error.message || t.depositModal.errors.defaultError);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 sm:p-6 text-white">
      {/* Чистое затемнение */}
      <div className="absolute inset-0 bg-black/60 transition-opacity touch-none" onClick={onClose} />
      
      {/* Основной контейнер - Горизонтальная форма */}
      <div className="relative w-full max-w-4xl bg-[#1e1e1e] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[500px] animate-in fade-in zoom-in-95 duration-200">
        
        <button 
          onClick={onClose} 
          disabled={isProcessing} 
          className="absolute top-4 right-4 p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 outline-none cursor-pointer z-[60]"
        >
          <X size={20} />
        </button>

        {/* Оверлеи (перекрывают весь блок) */}
        {(isProcessing || waitingForPayment || paymentSuccess) && (
          <div className="absolute inset-0 z-50 bg-[#1e1e1e] flex flex-col items-center justify-center p-8 text-center">
            
            {paymentSuccess && (
              <div className="max-w-sm w-full">
                <Check className="text-green-500 w-16 h-16 mx-auto mb-6" />
                <h3 className="text-2xl font-medium text-white mb-2">{t.depositModal.states.successTitle}</h3>
                <p className="text-zinc-400 mb-8">{t.depositModal.states.successDesc}</p>
              </div>
            )}

            {waitingForPayment && !paymentSuccess && (
              <div className="max-w-sm w-full">
                <ExternalLink className="text-blue-500 w-16 h-16 mx-auto mb-6" />
                <h3 className="text-2xl font-medium text-white mb-2">{t.depositModal.states.waitingTitle}</h3>
                <p className="text-zinc-400 mb-6">{t.depositModal.states.waitingDesc}</p>
                {paymentUrl && (
                  <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="block w-full py-3 mb-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors text-center">
                    Перейти к оплате
                  </a>
                )}
                <button onClick={() => setWaitingForPayment(false)} className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors cursor-pointer">
                  {t.depositModal.states.cancelBtn}
                </button>
              </div>
            )}

            {isProcessing && !waitingForPayment && (
              <div className="max-w-sm w-full flex flex-col items-center">
                <Loader2 size={32} className="text-blue-500 animate-spin mb-4" />
                <span className="text-zinc-300 font-medium">{t.depositModal.states.creatingPayment}</span>
              </div>
            )}
          </div>
        )}

        {/* ЛЕВАЯ КОЛОНКА (Конфигурация) */}
        <div className="w-full md:w-[45%] bg-[#1a1a1a] p-6 md:p-8 flex flex-col border-b md:border-b-0 md:border-r border-white/5">
          <div className="mb-8 mt-2">
            <h2 className="text-2xl font-medium text-white tracking-tight leading-tight">{t.depositModal.header.titleDeposit}</h2>
            <p className="text-sm text-zinc-400 mt-2">
              {t.depositModal.header.balance} <span className="text-white font-medium">{user?.crystals || 0} 💎</span>
            </p>
          </div>

          <div className="flex items-center gap-4 p-4 bg-[#121212] rounded-xl border border-white/5 mb-8">
            {user?.nickname ? (
              <img 
                src={`https://sg-public-api.royolands.com/rpweb/morda/${user.nickname}`} 
                alt={user.nickname} 
                className="w-12 h-12 rounded-lg bg-[#2a2a2a]"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-[#2a2a2a]" />
            )}
            <div>
              <div className="text-xs text-zinc-500 mb-0.5">Игровой аккаунт</div>
              <div className="text-white font-medium">{user?.nickname || 'Неизвестно'}</div>
            </div>
          </div>

          <div className="mb-4 flex items-center justify-between text-sm">
            <span className="text-zinc-400">{t.depositModal.form.crystals}</span>
            <span className="text-blue-400 font-medium">1 💎 = {RATES[currency]} {sign}</span>
          </div>

          {/* Отображение выбранной суммы */}
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6 flex items-center justify-center gap-3 mb-6">
            <span className="text-4xl font-medium text-white">{amount}</span>
            <span className="text-3xl text-zinc-500">💎</span>
          </div>

          {/* Выбор пакетов */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {PACKS.map((val) => (
              <button 
                key={val} 
                onClick={() => setAmount(val)} 
                className={`py-3 rounded-lg text-sm font-medium transition-colors outline-none cursor-pointer ${
                  amount === val 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-[#121212] border border-white/5 text-zinc-400 hover:bg-[#2a2a2a] hover:text-white'
                }`}
              >
                {val}
              </button>
            ))}
          </div>

          <div className="mt-auto pt-6 text-xs text-zinc-500 flex items-center gap-2">
            <Check size={14} className="text-green-500" />
            {t.depositModal.footer.secure}
          </div>
        </div>

        {/* ПРАВАЯ КОЛОНКА (Методы оплаты) */}
        <div className="w-full md:w-[55%] p-6 md:p-8 flex flex-col bg-[#1e1e1e] overflow-y-auto custom-scrollbar">
          
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-medium text-white">{t.depositModal.header.titleMethod}</h3>
            <span className="text-sm text-zinc-400">
              К оплате: <span className="text-white font-medium">{price} {sign}</span>
            </span>
          </div>

          {isLoadingCurrency ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-[#121212] rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {primaryMethods.map(method => (
                <PaymentButton 
                  key={method.id} 
                  method={method} 
                  amount={amount} 
                  currency={currency} 
                  description={t.depositModal.paymentMethods[method.descKey]}
                  onSelect={(m) => proceedToPay(m.id, amount)} 
                />
              ))}

              {secondaryMethods.length > 0 && (
                <>
                  <button 
                    onClick={() => setShowAllMethods(!showAllMethods)} 
                    className="w-full py-3 flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors cursor-pointer outline-none"
                  >
                    {showAllMethods ? t.depositModal.form.hideMethods : t.depositModal.form.showAllMethods}
                    <ChevronDown size={16} className={`transition-transform duration-300 ${showAllMethods ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showAllMethods && (
                    <div className="space-y-3 pt-1">
                      {secondaryMethods.map(method => (
                        <PaymentButton 
                          key={method.id} 
                          method={method} 
                          amount={amount} 
                          currency={currency} 
                          description={t.depositModal.paymentMethods[method.descKey]}
                          onSelect={(m) => proceedToPay(m.id, amount)} 
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}